import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useTheme } from "../lib/theme";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Blog", to: "/blog" },
  { label: "Company Profile", to: "/company-profile" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 80], [0.5, 0.85]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const onNav = (item) => {
    setOpen(false);
    if (item.to) {
      navigate(item.to);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + item.href);
    } else {
      document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      style={{ "--alpha": opacity }}
      className="glass-nav fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div className="container-apex h-16 md:h-20 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="nav-logo-home" className="hover:opacity-80 transition">
          <Logo size={36} />
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => onNav(n)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-foreground rounded-full hover:bg-secondary transition-all"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-[#16A34A] transition"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            onClick={() => navigate("/book")}
            data-testid="nav-book-consultation"
            className="btn-accent hidden md:inline-flex !px-5 !py-2 text-sm"
          >
            Book Consultation
          </button>
          <button
            className="lg:hidden w-10 h-10 rounded-full border border-border flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="container-apex py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => onNav(n)}
                className="text-left px-4 py-3 rounded-xl hover:bg-secondary font-semibold"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => { setOpen(false); navigate("/book"); }}
              className="btn-accent justify-center mt-2"
              data-testid="mobile-nav-book"
            >
              Book Consultation
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
